import { DropdownMenu, Popover as RPopover, Tabs as RTabs, Tooltip as RTooltip } from "radix-ui";
import type { ReactNode } from "react";
import { Toaster as Sonner, toast } from "sonner";
import { cn } from "../lib/cn";

export { toast };

export function Toaster() {
  return <Sonner position="top-center" richColors closeButton toastOptions={{ className: "font-sans" }} />;
}

export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "right" | "bottom" | "left" }) {
  return (
    <RTooltip.Provider delayDuration={300}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content side={side} sideOffset={6} className="z-[70] max-w-xs rounded-md bg-fg px-2.5 py-1.5 text-xs text-bg shadow-lg">
            {content}
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}

/**
 * Всплывающая панель по клику (подсказка с текстом и ссылками, мини-форма). В отличие от Tooltip — интерактивная,
 * открывается касанием на телефоне, закрывается Esc и кликом снаружи.
 */
export function Popover({ trigger, children, side = "bottom", align = "center", className, open, onOpenChange }: {
  trigger: ReactNode; children: ReactNode; side?: "top" | "right" | "bottom" | "left"; align?: "start" | "center" | "end"; className?: string;
  open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  return (
    <RPopover.Root open={open} onOpenChange={onOpenChange}>
      <RPopover.Trigger asChild>{trigger}</RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content side={side} align={align} sideOffset={8} collisionPadding={12}
          className={cn("z-[70] w-80 max-w-[calc(100vw-24px)] rounded-tile border border-border bg-surface p-4 text-sm text-fg shadow-xl outline-none animate-[ui-in_0.15s_ease-out]", className)}>
          {children}
          <RPopover.Arrow className="fill-surface" width={12} height={6} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

export type MenuItem =
  | { type?: "item"; label: ReactNode; icon?: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean; shortcut?: string }
  | { type: "separator" }
  | { type: "label"; label: ReactNode };

/** Меню действий «⋯» для строк таблиц, карточек. */
export function Menu({ trigger, items, align = "end" }: { trigger: ReactNode; items: MenuItem[]; align?: "start" | "end" }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align={align} sideOffset={6} className="z-[60] min-w-48 rounded-tile border border-border bg-surface p-1 text-fg shadow-xl data-[state=open]:animate-in">
          {items.map((it, i) => {
            if (it.type === "separator") return <DropdownMenu.Separator key={i} className="my-1 h-px bg-border" />;
            if (it.type === "label") return <DropdownMenu.Label key={i} className="px-3 py-1.5 text-xs text-muted">{it.label}</DropdownMenu.Label>;
            return (
              <DropdownMenu.Item
                key={i}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(
                  "flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-2  [&_svg]:size-4",
                  it.danger && "text-danger",
                )}
              >
                {it.icon}
                <span className="flex-1">{it.label}</span>
                {it.shortcut && <kbd className="text-xs text-muted">{it.shortcut}</kbd>}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export type TabItem<V extends string> = { value: V; label: ReactNode; content: ReactNode; badge?: ReactNode; disabled?: boolean };

/** Вкладки с горизонтальной прокруткой на узких экранах. */
export function Tabs<V extends string>({ value, onValueChange, items, className }: { value: V; onValueChange: (v: V) => void; items: TabItem<V>[]; className?: string }) {
  return (
    <RTabs.Root value={value} onValueChange={(v) => onValueChange(v as V)} className={className}>
      <RTabs.List className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0">
        {items.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            disabled={t.disabled}
            className="-mb-px inline-flex h-10 shrink-0 items-center gap-2 border-b-2 border-transparent px-3 text-sm font-medium whitespace-nowrap text-muted hover:text-fg disabled:opacity-50 data-[state=active]:border-primary data-[state=active]:text-fg"
          >
            {t.label}
            {t.badge}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {items.map((t) => (
        <RTabs.Content key={t.value} value={t.value} className="pt-4 outline-none">
          {t.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}
